import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SCREEN_PATHS } from '@/shared/routes';
import { Card } from '@/shared/ui/Card';
import { DataTable, PrimaryCell } from '@/shared/ui/DataTable';

import { profileReading } from '../model/catalogue';
import { PiiCell } from './PiiCell';
import { ProfileCell } from './ProfileCell';
import type { CatalogRow, DatasetFacts } from '@/shared/api/generated/catalog';

/* The Profiling and PII Classification views of one source's page.
 *
 * **Two tables, and neither renders a verdict of its own.** `ProfileCell` and `PiiCell` already
 * exist and already hold the two rules that matter: a profile reading distinguishes measured from
 * profiled-empty from never-profiled from unread, and a PII cell reads `classified` *before*
 * `pii_columns` so a dataset nobody examined is never shown as having no personal data. Re-deriving
 * either here would be a second answer to "is this clean", which is the one place a disagreement is
 * a compliance problem rather than a cosmetic one.
 *
 * **The data is the page's, not a new read.** Both tables take the rows and facts `SourceDatasets`
 * has already fetched for its own columns, so switching tabs issues no requests and the three views
 * cannot disagree about what they are describing. That is also what makes these views meaningful
 * *before* any bulk run: they render the state the backend persists, so a source profiled last week
 * by `lumiq-cli` reads correctly here without anybody pressing anything.
 */

export interface AnalysisTableProps {
  readonly rows: readonly CatalogRow[];
  readonly facts: ReadonlyMap<string, DatasetFacts> | undefined;
  /** True while the facts read is in flight, so a row can say "not yet known" rather than "never". */
  readonly factsPending: boolean;
  /** True when the facts read failed — the only case that is honestly *unread*. */
  readonly factsUnread: boolean;
}

function QualifiedName({ row }: { readonly row: CatalogRow }): ReactElement {
  return (
    <PrimaryCell>
      {/* The same link the main table uses, so a reader who spots an unprofiled table in this view
          reaches it the same way. Its detail screen is where the individual actions live. */}
      <Link className="lq-cell__primary" to={`${SCREEN_PATHS.datasets}/${row.id}`}>
        <span className="lq-cell__prefix">{row.schema_name}.</span>
        {row.object_name}
      </Link>
    </PrimaryCell>
  );
}

export function ProfilingTable({
  rows,
  facts,
  factsPending,
  factsUnread,
}: AnalysisTableProps): ReactElement {
  const { t } = useTranslation();
  return (
    <Card flush>
      <DataTable
        caption={t('catalogue.profiling.tableCaption')}
        columns={[
          { key: 'name', label: t('catalog.column.name') },
          { key: 'rows', label: t('catalogue.column.rows'), numeric: true },
          { key: 'profiled', label: t('catalogue.column.lastProfiled') },
        ]}
      >
        {rows.map((row) => {
          const reading = profileReading(facts?.get(row.id), { unread: factsUnread });
          return (
            <tr key={row.id}>
              <td>
                <QualifiedName row={row} />
              </td>
              <td className="lq-table__num lumiq-numeric">
                <ProfileCell reading={reading} show="rows" pending={factsPending} />
              </td>
              <td>
                <ProfileCell reading={reading} show="when" pending={factsPending} />
              </td>
            </tr>
          );
        })}
      </DataTable>
    </Card>
  );
}

export function PiiTable({
  rows,
}: {
  readonly rows: readonly CatalogRow[];
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Card flush>
      <DataTable
        caption={t('catalogue.pii.tableCaption')}
        columns={[
          { key: 'name', label: t('catalog.column.name') },
          { key: 'pii', label: t('catalog.column.pii') },
          { key: 'columns', label: t('catalogue.column.piiColumns'), numeric: true },
        ]}
      >
        {rows.map((row) => (
          <tr key={row.id}>
            <td>
              <QualifiedName row={row} />
            </td>
            <td>
              <PiiCell row={row} />
            </td>
            <td className="lq-table__num lumiq-numeric">
              {/* **A dash, not a zero, until the dataset has been classified.** `pii_columns` is 0
                  for a dataset nobody examined and 0 for one the classifier cleared, and the
                  contract says so in as many words — rendering the count alone shows those two
                  identically, and the first is not a clean bill of health. `PiiCell` beside this
                  makes the same distinction in its own language; this column simply refuses to
                  contribute a number that has no meaning yet. */}
              {row.classified ? (
                row.pii_columns
              ) : (
                <span className="lq-muted" title={t('catalog.pii.unclassified.meaning')}>
                  {t('catalogue.pii.noCount')}
                </span>
              )}
            </td>
          </tr>
        ))}
      </DataTable>
    </Card>
  );
}
